import { useQuery } from '@tanstack/react-query';
import { adminApi, type DashboardSummary } from '@/lib/adminApi';
import { StatTile, Card, EmptyState, Skeleton, cn } from '@/components/ui';
import {
  IconCar,
  IconUsers,
  IconTrips,
  IconWallet,
  IconReceipt,
  IconFlag,
  IconChat,
  IconRefresh,
  IconDashboard,
} from '@/components/ui/icons';
import { AdminPage } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';

/** Refetch the snapshot every 60s so the landing stays roughly live without
 *  hammering the roll-up (each figure is its own sub-query server-side). */
const REFRESH_MS = 60_000;

/** AUD formatter — thousands-grouped, 2dp (these are platform-wide totals, so
 *  grouping keeps big numbers readable: $18,423.50 not $18423.50). */
function aud(n: number): string {
  return `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** km-credit formatter — thousands-grouped, 2dp + " km" (platform-wide pool). */
function kmCredit(n: number): string {
  return `${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km`;
}

/** Group-separated whole-number formatter (e.g. 12,480). */
function count(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return n.toLocaleString();
}

/** A labelled band of stat cards: a small section heading over a responsive
 *  grid. Keeps the page scannable (People / Operations / Finance / Queues). */
function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-7 last:mb-0">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="text-[15px] font-semibold text-text-primary">{title}</h3>
        {hint ? <span className="text-xs text-text-secondary">{hint}</span> : null}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">{children}</div>
    </section>
  );
}

/** Dashboard — the platform overview landing. Binds a row of stat cards to
 *  GET /v1/admin/dashboard/summary (driver/customer/trip counts, today's
 *  revenue, the two wallet pools, and the open complaint/support queues).
 *
 *  Every figure fails soft to 0 server-side, so this never errors on a
 *  partially-migrated Postgres — a card just shows 0 (or, for a genuinely
 *  unreachable source, an em dash) rather than breaking. Skeleton loaders cover
 *  the first fetch; a manual refresh + a 60s auto-refresh keep it current. */
export function Dashboard() {
  const { data, isLoading, isError, error, isFetching, refetch, dataUpdatedAt } =
    useQuery({
      queryKey: ['admin', 'dashboard', 'summary'],
      queryFn: ({ signal }) => adminApi.dashboardSummary(signal),
      refetchInterval: REFRESH_MS,
      placeholderData: (prev) => prev,
    });

  return (
    <AdminPage>
      {/* Header — title + last-updated + manual refresh. */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold text-text-primary">Platform overview</h2>
          <p className="mt-1 text-[13px] text-text-secondary">
            A live snapshot of drivers, customers, trips, money and the open support queues.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <LastUpdated iso={data?.last_updated} fetchedAt={dataUpdatedAt} loading={isLoading} />
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 rounded-lg border border-hairline bg-surface px-2.5 py-1.5 text-[13px] font-medium text-text-secondary transition-colors hover:bg-hover hover:text-text-primary disabled:opacity-50"
          >
            <IconRefresh className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {isError ? (
        <Card>
          <EmptyState
            title="Couldn't load the dashboard"
            description={
              (error as Error)?.message ??
              'The platform summary is temporarily unavailable. Try refreshing.'
            }
            icon={<IconDashboard className="h-6 w-6" />}
          />
        </Card>
      ) : isLoading ? (
        <DashboardSkeleton />
      ) : (
        <DashboardBody s={data as DashboardSummary} />
      )}
    </AdminPage>
  );
}

/** The resolved cards, grouped into bands. */
function DashboardBody({ s }: { s: DashboardSummary }) {
  // Derived: share of drivers approved (a quiet trend hint, not a card of its own).
  const approvedPct =
    s.total_drivers > 0 ? Math.round((s.approved_drivers / s.total_drivers) * 100) : null;

  return (
    <>
      {/* People */}
      <Section title="People">
        <StatTile
          label="Drivers"
          value={count(s.total_drivers)}
          hint={approvedPct === null ? 'No drivers yet' : `${approvedPct}% approved`}
          icon={<IconCar className="h-5 w-5" />}
        />
        <StatTile
          label="Approved drivers"
          value={count(s.approved_drivers)}
          hint="Cleared to accept trips"
          tone={s.approved_drivers > 0 ? 'ok' : 'neutral'}
        />
        <StatTile
          label="Pending review"
          value={count(s.pending_drivers)}
          hint={s.pending_drivers > 0 ? 'Awaiting document review' : 'Queue clear'}
          tone={s.pending_drivers > 0 ? 'warn' : 'neutral'}
        />
        <StatTile
          label="Online now"
          value={count(s.online_drivers)}
          hint="Live in the last minute"
          tone={s.online_drivers > 0 ? 'info' : 'neutral'}
          icon={<IconUsers className="h-5 w-5" />}
        />
      </Section>

      {/* Operations */}
      <Section title="Operations">
        <StatTile
          label="Customers"
          value={count(s.total_customers)}
          hint="Registered riders"
          icon={<IconUsers className="h-5 w-5" />}
        />
        <StatTile
          label="Trips today"
          value={count(s.trips_today)}
          hint={s.trips_today > 0 ? 'Since midnight' : 'None yet today'}
          icon={<IconTrips className="h-5 w-5" />}
        />
        <StatTile
          label="Trips all-time"
          value={count(s.trips_total)}
          hint="Completed + in-flight"
        />
        <StatTile
          label="Revenue today"
          value={aud(s.revenue_today_aud)}
          hint="Fares booked since midnight"
          tone={s.revenue_today_aud > 0 ? 'ok' : 'neutral'}
          icon={<IconReceipt className="h-5 w-5" />}
        />
      </Section>

      {/* Finance — wallet pools (the two km-credit / earnings floats). */}
      <Section title="Wallet pools" hint="Outstanding balances across all accounts">
        <StatTile
          label="Customer km-credit"
          value={kmCredit(s.wallet_customer_km)}
          hint="Total rider credit on hand"
          icon={<IconWallet className="h-5 w-5" />}
        />
        <StatTile
          label="Driver earnings"
          value={aud(s.wallet_driver_km)}
          hint="Total driver balance awaiting payout"
          icon={<IconWallet className="h-5 w-5" />}
        />
      </Section>

      {/* Queues — the two things a staffer should clear. */}
      <Section title="Open queues">
        <StatTile
          label="Open complaints"
          value={count(s.open_complaints)}
          hint={s.open_complaints > 0 ? 'Need a resolution' : 'Nothing open'}
          tone={s.open_complaints > 0 ? 'danger' : 'ok'}
          icon={<IconFlag className="h-5 w-5" />}
        />
        <StatTile
          label="Open support tickets"
          value={count(s.open_support_tickets)}
          hint={s.open_support_tickets > 0 ? 'Awaiting an agent' : 'Inbox clear'}
          tone={s.open_support_tickets > 0 ? 'warn' : 'ok'}
          icon={<IconChat className="h-5 w-5" />}
        />
      </Section>
    </>
  );
}

/** "Last updated …" — prefers the server's snapshot time, falls back to when
 *  the client received the response. */
function LastUpdated({
  iso,
  fetchedAt,
  loading,
}: {
  iso?: string;
  fetchedAt?: number;
  loading: boolean;
}) {
  if (loading) return <Skeleton className="h-4 w-40" />;
  const when = iso || (fetchedAt ? new Date(fetchedAt).toISOString() : undefined);
  return (
    <span className="hidden text-xs text-text-secondary sm:inline">
      Last updated {dateTime(when)}
    </span>
  );
}

/** First-load placeholder — mirrors the real layout (4 bands of tiles) so the
 *  page doesn't jump when data arrives. */
function DashboardSkeleton() {
  const band = (cols: number, key: string) => (
    <section key={key} className="mb-7">
      <Skeleton className="mb-3 h-4 w-28" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: cols }).map((_, i) => (
          <div key={i} className="panel p-5">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="mt-3 h-7 w-24" />
            <Skeleton className="mt-2 h-3 w-28" />
          </div>
        ))}
      </div>
    </section>
  );
  return (
    <>
      {band(4, 'a')}
      {band(4, 'b')}
      {band(2, 'c')}
      {band(2, 'd')}
    </>
  );
}
